import Button from "@/dashboard/core/components/Button";
import { Link, useLocation } from "react-router-dom";
import React from "react";
import SidebarNavItem from "@/dashboard/shared/components/Sidebar/SidebarNavItem";
import { compose } from "redux";
import { connect } from "react-redux";
import styled from "styled-components";
import theme from "@/styles/theme";

const Container = styled.div`
  position: sticky;
  top: 0;
  left: 0;
  width: 228px;
`;

const OptionsContainer = styled.div`
  padding: 0 20px 0 ${theme.spacing.unit * 1}px;
`;

const Option = styled(Button)`
  margin: ${theme.spacing.unit * 1}px 0;
`;

const StyledLink = styled(Link)`
  display: block;
`;

// Individual functions for each settings nav item - focused on when they SHOULD be visible
const addProfileOption = (options, config, location) => {
  // Profile is available to all users
  options.push({
    title: "Profile",
    path: "/employers/settings/profile",
    isSelected: location.pathname.startsWith("/employers/settings/profile"),
  });
};

const addAtsSetupOption = (options, config, location) => {
  // You can't see ATS setup if you have Puck ATS or you're not an admin
  if (config.hasPuckAts || !config.isAdmin) {
    return;
  }

  if (config.hasJobPages || config.isSelfServe) {
    options.push({
      title: "ATS set up",
      path: "/employers/settings/ats",
      isSelected: location.pathname.startsWith("/employers/settings/ats"),
    });
  }
};

const addDepartmentsOption = (options, config, location) => {
  if (config.isGuest) {
    return;
  }

  if (config.hasPuckAts) {
    options.push({
      title: "Departments",
      path: "/employers/settings/departments",
      isSelected: location.pathname.startsWith(
        "/employers/settings/departments"
      ),
    });
  }
};

const addOfficesOption = (options, config, location) => {
  if (config.isGuest) {
    return;
  }

  if (config.hasPuckAts) {
    options.push({
      title: "Offices",
      path: "/employers/settings/offices",
      isSelected: location.pathname.startsWith("/employers/settings/offices"),
    });
  }
};

const addMembersOption = (options, config, location) => {
  if (config.isGuest) {
    return;
  }

  options.push({
    title: "Members",
    path: "/employers/settings/members",
    isSelected: location.pathname.startsWith("/employers/settings/members"),
  });
};

const addSmartLinksOption = (options, config, location) => {
  if (config.hasPuckAts || !config.isSelfServe) {
    options.push({
      title: "Smart links",
      path: "/employers/settings/smart-links",
      isSelected: location.pathname.startsWith(
        "/employers/settings/smart-links"
      ),
    });
  }
};

const addEmailOption = (options, config, location) => {
  if (config.isGuest) {
    return;
  }

  // If you're self-serve and don't have Puck ATS, you can't see email
  if (config.isSelfServe && !config.hasPuckAts) {
    return;
  }

  // crmEnabled defaults to true
  if (config.crmEnabled) {
    options.push({
      title: "Email",
      path: "/employers/settings/email",
      isSelected: location.pathname.startsWith("/employers/settings/email"),
    });
  }
};

// Main function to build settings sidebar options
const buildSettingsOptions = (user, location) => {
  const config = {
    crmEnabled: user.employerProfile.crmEnabled,
    hasPuckAts: user.employerProfile.hasPuckAts,
    hasJobPages: user.hasJobPages,
    isSelfServe: user.employerProfile.subscriptionTier === "free",
    isAdmin: user.role === "admin",
    isGuest: user.role === "guest",
  };

  const options = [];

  // Add each settings nav item using individual functions
  addProfileOption(options, config, location);
  addAtsSetupOption(options, config, location);
  addDepartmentsOption(options, config, location);
  addOfficesOption(options, config, location);
  addMembersOption(options, config, location);
  addSmartLinksOption(options, config, location);
  addEmailOption(options, config, location);

  return options;
};

const SettingsSidebar = (props) => {
  const { user } = props;
  const location = useLocation();

  if (!user) {
    return null;
  }

  const options = buildSettingsOptions(user, location);

  return (
    <>
      <Container data-testing="settings-nav">
        <OptionsContainer>
          {options.map((option) => {
            if (option.onClick) {
              return (
                <Option
                  key={option.title}
                  isLarge
                  isDashboard
                  isSelected={option.isSelected}
                  onClick={option.onClick}
                >
                  {option.title}
                </Option>
              );
            } else {
              return (
                <StyledLink key={option.title} to={option.path}>
                  <SidebarNavItem key={option.title} {...option} />
                </StyledLink>
              );
            }
          })}
        </OptionsContainer>
      </Container>
    </>
  );
};

function mapStateToProps(state) {
  const { auth } = state;

  return {
    user: auth.user,
    subscriptionTier: auth.user?.employerProfile.subscriptionTier,
  };
}

const enhance = compose(connect(mapStateToProps));

export default enhance(SettingsSidebar);
